/**
 * Spec §11 PR 10 — typed wrapper around `operationsClient` that
 * auto-attaches an `Idempotency-Key` header on every money-moving POST
 * (per spec §11.1).
 *
 * Why a separate file? The default `operationsClient` interceptors are
 * for auth / tenant injection. Adding an idempotency interceptor on the
 * shared client would attach the header to every POST in the app,
 * including non-billing routes that don't expect it. This wrapper makes
 * the dependency explicit per call site and keeps blast radius small
 * during the deprecation window.
 *
 * UUIDv7 generator: the project does not yet depend on the `uuid`
 * package. We inline a small UUIDv7 implementation per RFC 9562 to
 * avoid adding another runtime dep just for a single header. Format:
 *   - bits 0-47: unix timestamp (ms)
 *   - bits 48-51: version (7)
 *   - bits 52-63: random
 *   - bits 64-65: variant (10)
 *   - bits 66-127: random
 *
 * The output is the canonical 8-4-4-4-12 hex form.
 */
import type { AxiosRequestConfig } from 'axios';
import { operationsClient, apiGet, apiPost, apiPatch } from '../client';
import { ApiError } from '../errors';
import { getActiveTenantId } from '@/lib/activeTenant';

// ── UUIDv7 generator ────────────────────────────────────────────────────────

/** RFC 9562 UUIDv7 — millisecond-precision timestamp + 74 random bits. */
export function uuidv7(): string {
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    // Fallback (Node < 18 / older test env): non-secure but deterministic-
    // enough for tests. Real browsers + Node ≥ 18 always have crypto.
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }

  const ts = BigInt(Date.now());
  // 48-bit timestamp in big-endian.
  bytes[0] = Number((ts >> 40n) & 0xffn);
  bytes[1] = Number((ts >> 32n) & 0xffn);
  bytes[2] = Number((ts >> 24n) & 0xffn);
  bytes[3] = Number((ts >> 16n) & 0xffn);
  bytes[4] = Number((ts >> 8n) & 0xffn);
  bytes[5] = Number(ts & 0xffn);

  // Version 7 in the upper nibble of byte 6.
  bytes[6] = (bytes[6]! & 0x0f) | 0x70;
  // Variant 10 in the upper two bits of byte 8.
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;

  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32),
  ].join('-');
}

// ── Idempotency-aware POST wrapper ──────────────────────────────────────────

const IDEMPOTENCY_TTL_MS = 24 * 60 * 60 * 1000;
const MAX_PENDING_REQUEST_KEYS = 1_000;

interface PendingRequestKey {
  key: string;
  expiresAt: number;
}

// A transport failure can happen after the server commits but before the
// browser receives the response. Keep that command's key so a React Query or
// user-triggered retry asks the backend to replay the same operation.
const pendingRequestKeys = new Map<string, PendingRequestKey>();

function stableRequestValue(value: unknown): unknown {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) return value.toJSON();
  if (Array.isArray(value)) return value.map(stableRequestValue);
  if (typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>).sort()) {
      result[key] = stableRequestValue((value as Record<string, unknown>)[key]);
    }
    return result;
  }
  return value;
}

function requestSignature(url: string, data: unknown, config?: AxiosRequestConfig): string {
  // Axios owns query serialization. Use the URI it will actually send so
  // URLSearchParams, custom serializers, arrays and nested values cannot
  // collapse into an unrelated in-memory signature.
  const serializedUrl = operationsClient.getUri({
    url,
    params: config?.params,
    paramsSerializer: config?.paramsSerializer,
  });
  return JSON.stringify({
    tenantId: getActiveTenantId(),
    url: serializedUrl,
    data: stableRequestValue(data),
  });
}

export function isIdempotencyMismatch(error: ApiError): boolean {
  if (error.status !== 409 || typeof error.data !== 'object' || !error.data) {
    return false;
  }
  return (error.data as { code?: unknown }).code === 'IDEMPOTENCY_KEY_MISMATCH';
}

/**
 * The server is still running the request that first claimed this key
 * (`IDEMPOTENCY_REQUEST_IN_PROGRESS`). `billingV2Post` hands an identical
 * in-flight request the same key, so a duplicate sent while the first is
 * running lands here. Retried shortly it either replays the first's answer
 * (same key) or, once the first has answered and released its key, runs
 * under a fresh one.
 */
export function isIdempotencyRequestInProgress(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 409) return false;
  if (typeof error.data !== 'object' || !error.data) return false;
  return (error.data as { code?: unknown }).code === 'IDEMPOTENCY_REQUEST_IN_PROGRESS';
}

/**
 * Whether an `Idempotency-Key` should be KEPT for an exact retry (`true`) —
 * the server gave no definitive answer, so the same key must be replayed —
 * or minted fresh next time (`false`) because it did.
 *
 * Shared by `billingV2Post` below (which manages its own in-memory key per
 * request signature) and any caller that manages its own key lifecycle for a
 * `@RequireIdempotency()` route this wrapper doesn't cover (e.g. the
 * Contact/Invoice/Job "charge a new card or bank account" flows, which hold
 * a key across a UI retry rather than a request signature) — one
 * classification, so the two never drift on what counts as "definitive".
 *
 * - Transport/status `0` (network error, timeout, cancel): no response was
 *   ever received — keep.
 * - `5xx`: the server may have committed before failing to reply — keep.
 * - `429`: rate-limited before being processed at all — keep.
 * - `409` that is NOT `IDEMPOTENCY_KEY_MISMATCH` (e.g. the lease-holder
 *   `IDEMPOTENCY_REQUEST_IN_PROGRESS`): the original request is still being
 *   processed under this exact key — keep.
 * - `409 IDEMPOTENCY_KEY_MISMATCH`: the same key arrived with a different
 *   request body/signature — a NEW attempt already invalidated this key, so
 *   mint fresh.
 * - Any other `4xx`: the server read the request and definitively refused
 *   it — mint fresh.
 */
/**
 * A charge refused before it reached the processor because it would take more
 * than the invoice or job still owes once the money already clearing is
 * counted (`CHARGE_EXCEEDS_BALANCE`). Nothing moved and the reason is in the
 * message, so it is a definitive answer, not an unknown outcome.
 */
export function isChargeExceedsBalance(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 409) return false;
  if (typeof error.data !== 'object' || !error.data) return false;
  return (error.data as { code?: unknown }).code === 'CHARGE_EXCEEDS_BALANCE';
}

export function isIdempotencyKeyRetryable(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  if (error.status === 0) return true;
  if (error.status >= 500) return true;
  if (error.status === 429) return true;
  if (error.status === 409) return !isIdempotencyMismatch(error) && !isChargeExceedsBalance(error);
  return false;
}

function pendingKeyFor(signature: string): string {
  const now = Date.now();
  const existing = pendingRequestKeys.get(signature);
  if (existing?.expiresAt && existing.expiresAt > now) return existing.key;
  if (existing) pendingRequestKeys.delete(signature);

  // Keep the hot path O(1). Only sweep the full map when it reaches its
  // defensive ceiling. Normal successful/definitive requests delete
  // themselves below.
  if (pendingRequestKeys.size >= MAX_PENDING_REQUEST_KEYS) {
    for (const [candidate, entry] of pendingRequestKeys) {
      if (entry.expiresAt <= now) pendingRequestKeys.delete(candidate);
    }
  }
  if (pendingRequestKeys.size >= MAX_PENDING_REQUEST_KEYS) {
    // Fail closed rather than evicting a still-ambiguous financial command:
    // forgetting it could let a later retry use a fresh key and duplicate the
    // server-side operation.
    throw new ApiError(
      0,
      'UNKNOWN',
      'Too many billing requests are awaiting confirmation. Wait for pending requests to settle before starting another.',
    );
  }

  const key = uuidv7();
  pendingRequestKeys.set(signature, {
    key,
    expiresAt: now + IDEMPOTENCY_TTL_MS,
  });
  return key;
}

/**
 * POST a money-moving request. Auto-attaches `Idempotency-Key: <uuidv7>`
 * unless the caller has explicitly set one in `config.headers`.
 *
 * Use this for every endpoint annotated with `@RequireIdempotency()` on
 * the backend (spec §11.1):
 *   - /v2/billing/payments (record + refund)
 *   - /v2/billing/deposits (capture + apply + refund)
 *   - /v2/billing/credit-memos (create + apply)
 *   - /v2/billing/payment-schedules (create + fire-rule)
 *   - /v2/billing/payment-methods/setup-intent
 */
export async function billingV2Post<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig,
): Promise<T> {
  const merged: AxiosRequestConfig = {
    ...config,
    headers: {
      ...(config?.headers ?? {}),
    },
  };
  // Don't override an explicit caller-provided key — useful for retries
  // that need to share the same key as the prior attempt.
  const headers = merged.headers as Record<string, string | undefined>;
  let managedSignature: string | undefined;
  let managedKey: string | undefined;
  if (!headers['Idempotency-Key'] && !headers['idempotency-key']) {
    managedSignature = requestSignature(url, data, config);
    managedKey = pendingKeyFor(managedSignature);
    headers['Idempotency-Key'] = managedKey;
  }

  try {
    const response = await apiPost<T, D>(operationsClient, url, data, merged);
    if (managedSignature && pendingRequestKeys.get(managedSignature)?.key === managedKey) {
      pendingRequestKeys.delete(managedSignature);
    }
    return response;
  } catch (error) {
    // A definitive client rejection proves this invocation did not leave an
    // unknown successful outcome. Network/timeout/cancel, 409/429 and 5xx do
    // not prove that, so their key remains available for an exact retry —
    // see `isIdempotencyKeyRetryable` for the shared classification.
    if (
      managedSignature &&
      error instanceof ApiError &&
      !isIdempotencyKeyRetryable(error) &&
      pendingRequestKeys.get(managedSignature)?.key === managedKey
    ) {
      pendingRequestKeys.delete(managedSignature);
    }
    throw error;
  }
}

/** PATCH passthrough — billing v2 PATCHes don't require idempotency keys. */
export async function billingV2Patch<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig,
): Promise<T> {
  return apiPatch<T, D>(operationsClient, url, data, config);
}

/** GET against the operations service (no idempotency key needed). */
export async function billingV2Get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  return apiGet<T>(operationsClient, url, config);
}

/**
 * Make a non-idempotent POST (e.g. `/v2/billing/qbo/sync/:entity/:id`).
 * Same client, no auto-key.
 */
export async function billingV2PostUnsafe<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig,
): Promise<T> {
  return apiPost<T, D>(operationsClient, url, data, config);
}
